import type { BrandSocialAffinities } from "./brand-profile.ts";
import type { SocialPostLayoutRecipeId } from "./social-post-layout-recipes.ts";
import {
  socialPostLayoutTemplateDefinitionSchema,
  type SocialPostLayoutTemplateAiPolicyV2,
  type SocialPostLayoutTemplateDefinitionV2,
  type SocialPostLayoutTemplateLayerV2,
} from "./social-post-layout-templates.ts";

type ImageSlot = SocialPostLayoutTemplateDefinitionV2["slots"]["imageSlots"][number];
type TextSlot = SocialPostLayoutTemplateDefinitionV2["slots"]["textSlots"][number];
type LogoSlot = SocialPostLayoutTemplateDefinitionV2["slots"]["logoSlot"];
type Control = SocialPostLayoutTemplateDefinitionV2["controls"][number];
type PreviewImages = SocialPostLayoutTemplateDefinitionV2["previewFill"]["images"];

export const SOCIAL_POST_LAYOUT_RECIPE_TEMPLATE_IDS = {
  full_bleed_lower_third: "10000000-0000-4000-8000-000000000001",
  full_bleed_side_plaque: "10000000-0000-4000-8000-000000000002",
  split_editorial: "10000000-0000-4000-8000-000000000003",
  poster_lockup: "10000000-0000-4000-8000-000000000004",
  magazine_caption_overlay: "10000000-0000-4000-8000-000000000005",
  image_stack_story: "10000000-0000-4000-8000-000000000006",
  quote_card_photo: "10000000-0000-4000-8000-000000000007",
  product_detail_callout: "10000000-0000-4000-8000-000000000008",
  announcement_strip: "10000000-0000-4000-8000-000000000009",
  minimal_logo_signature: "10000000-0000-4000-8000-000000000010",
} as const satisfies Record<SocialPostLayoutRecipeId, string>;

const CANVAS = { width: 1080, height: 1350, backgroundFill: "#f7f3ec" } as const;
const FONTS = [
  {
    role: "heading",
    family: "Fraunces",
    source: "google_font",
    selectedVariant: "700",
  },
  {
    role: "body",
    family: "DM Sans",
    source: "google_font",
    selectedVariant: "400",
  },
] as const;

const imageContentOnlyPolicy: SocialPostLayoutTemplateAiPolicyV2 = {
  mode: "content_only",
  allowedFields: [],
  allowHideWhenOptional: false,
  brandColorBias: "strong",
};

const textFitPolicy: SocialPostLayoutTemplateAiPolicyV2 = {
  mode: "art_directed",
  allowedFields: ["x", "y", "width", "height", "fontSize", "lineHeight", "letterSpacing", "align"],
  bounds: "safe_area",
  allowHideWhenOptional: false,
  brandColorBias: "strong",
};

const logoFitPolicy: SocialPostLayoutTemplateAiPolicyV2 = {
  mode: "resize_move",
  allowedFields: ["x", "y", "width", "height"],
  bounds: "safe_area",
  allowHideWhenOptional: false,
  brandColorBias: "strong",
};

const staticStylePolicy: SocialPostLayoutTemplateAiPolicyV2 = {
  mode: "style",
  allowedFields: ["fill", "stroke", "strokeWidth", "cornerRadius", "opacity"],
  allowHideWhenOptional: false,
  brandColorBias: "strong",
};

const carrierRectPolicy: SocialPostLayoutTemplateAiPolicyV2 = {
  mode: "art_directed",
  allowedFields: [
    "x",
    "y",
    "width",
    "height",
    "fill",
    "stroke",
    "strokeWidth",
    "cornerRadius",
    "opacity",
  ],
  bounds: "canvas",
  allowHideWhenOptional: false,
  brandColorBias: "strong",
};

const isFullCanvasRect = (x: number, y: number, width: number, height: number): boolean =>
  x === 0 && y === 0 && width === CANVAS.width && height === CANVAS.height;

const heroImageSlot = (purpose: string, safeZoneRole: ImageSlot["safeZoneRole"] = "none") =>
  ({
    id: "hero_image",
    label: "Hero image",
    kind: "hero",
    requirement: "required",
    minCount: 1,
    maxCount: 1,
    purpose,
    safeZoneRole,
  }) satisfies ImageSlot;

const supportingImageSlot = (purpose: string) =>
  ({
    id: "supporting_images",
    label: "Supporting image",
    kind: "supporting",
    requirement: "required",
    minCount: 1,
    maxCount: 1,
    purpose,
    safeZoneRole: "none",
  }) satisfies ImageSlot;

const headlineTextSlot = (
  suggestedMaxLength: number,
  requirement: TextSlot["requirement"] = "required",
) =>
  ({
    id: "headline",
    label: "Headline",
    kind: "headline",
    requirement,
    purpose: "Primary overlay headline.",
    suggestedMaxLength,
  }) satisfies TextSlot;

const bodyTextSlot = (
  suggestedMaxLength: number,
  requirement: TextSlot["requirement"] = "optional",
) =>
  ({
    id: "body",
    label: "Body copy",
    kind: "body",
    requirement,
    purpose: "Short supporting copy when it improves the creative.",
    suggestedMaxLength,
  }) satisfies TextSlot;

const optionalLogoSlot = (purpose: string): LogoSlot => ({
  requirement: "optional",
  purpose,
  preferredPlacements: ["top_left", "top_right", "bottom_left", "bottom_right"],
});

const control = (
  id: string,
  label: string,
  kind: Control["kind"],
  values: readonly string[],
  purpose: string,
): Control => ({
  id,
  label,
  kind,
  values: values.map((value) => ({ id: value, label: value.replaceAll("_", " ") })),
  defaultValue: values[0],
  purpose,
});

const rectLayer = (
  id: string,
  x: number,
  y: number,
  width: number,
  height: number,
  fill: string,
  zIndex: number,
  cornerRadius = 0,
): SocialPostLayoutTemplateLayerV2 => ({
  id,
  type: "rect",
  x,
  y,
  width,
  height,
  rotation: 0,
  opacity: 1,
  zIndex,
  visible: true,
  fill,
  strokeWidth: 0,
  cornerRadius,
  binding: { type: "static" },
  optional: false,
  aiPolicy: isFullCanvasRect(x, y, width, height) ? staticStylePolicy : carrierRectPolicy,
});

const imageLayer = (
  id: string,
  x: number,
  y: number,
  width: number,
  height: number,
  slotId = "hero_image",
  index = 0,
  zIndex = 0,
  options?: { opacity?: number; cornerRadius?: number; prompt?: string },
): SocialPostLayoutTemplateLayerV2 => ({
  id,
  type: "image",
  x,
  y,
  width,
  height,
  rotation: 0,
  opacity: options?.opacity ?? 1,
  zIndex,
  visible: true,
  source: {
    kind: "generated_prompt",
    prompt: options?.prompt ?? "Editorial brand image with a clear copy-safe composition.",
    backgroundMode: "opaque",
  },
  fit: "cover",
  cornerRadius: options?.cornerRadius ?? 0,
  binding: { type: "imageSlot", slotId, index },
  optional: false,
  aiPolicy: imageContentOnlyPolicy,
});

const textLayer = (
  id: string,
  slotId: string,
  text: string,
  x: number,
  y: number,
  width: number,
  height: number,
  fontSize: number,
  zIndex: number,
  options?: {
    optional?: boolean;
    fill?: string;
    family?: "Fraunces" | "DM Sans";
    align?: "left" | "center" | "right";
    lineHeight?: number;
    letterSpacing?: number;
  },
): SocialPostLayoutTemplateLayerV2 => ({
  id,
  type: "text",
  x,
  y,
  width,
  height,
  rotation: 0,
  opacity: 1,
  zIndex,
  visible: true,
  text,
  fill: options?.fill ?? "#161411",
  fontFamily: options?.family ?? "Fraunces",
  fontSize,
  fontStyle: "normal",
  align: options?.align ?? "left",
  verticalAlign: "top",
  lineHeight: options?.lineHeight ?? 1.08,
  letterSpacing: options?.letterSpacing ?? -0.5,
  padding: 0,
  binding: { type: "textSlot", slotId },
  optional: options?.optional ?? false,
  aiPolicy: textFitPolicy,
});

const logoLayer = (
  x: number,
  y: number,
  width: number,
  height: number,
  zIndex: number,
): SocialPostLayoutTemplateLayerV2 => ({
  id: "logo",
  type: "logo",
  x,
  y,
  width,
  height,
  rotation: 0,
  opacity: 1,
  zIndex,
  visible: true,
  source: "brand_logo",
  fit: "contain",
  cornerRadius: 0,
  binding: { type: "logoSlot" },
  optional: true,
  aiPolicy: logoFitPolicy,
});

const logoCarrierLayer = (
  x: number,
  y: number,
  width: number,
  height: number,
  zIndex: number,
): SocialPostLayoutTemplateLayerV2 =>
  rectLayer("logo_carrier", x, y, width, height, "#fffaf2", zIndex, 24);

type TemplateKind =
  | "lower_third"
  | "side_plaque"
  | "split_editorial"
  | "poster_lockup"
  | "caption_overlay"
  | "image_stack"
  | "quote_card"
  | "product_callout"
  | "announcement_strip"
  | "minimal_signature";

interface TemplatePresetSpec {
  id: string;
  name: string;
  description: string;
  kind: TemplateKind;
  bestFor: readonly string[];
  avoidWhen: readonly string[];
  socialAffinities: BrandSocialAffinities;
  imageSlots: readonly ImageSlot[];
  textSlots: readonly TextSlot[];
  logoSlot: LogoSlot;
  controls: readonly Control[];
  previewText: Record<string, string>;
  previewImages: PreviewImages;
  previewControls?: Record<string, string>;
}

const createTemplateLayers = (kind: TemplateKind): SocialPostLayoutTemplateLayerV2[] => {
  switch (kind) {
    case "lower_third":
      return [
        imageLayer("hero_image", 0, 0, 1080, 1350, "hero_image", 0, 0),
        rectLayer("copy_plaque", 80, 900, 760, 270, "#f7f3ec", 1, 28),
        textLayer("headline", "headline", "A calmer launch", 120, 942, 680, 94, 54, 2),
        textLayer(
          "body",
          "body",
          "Make the moment easy to understand.",
          120,
          1046,
          620,
          72,
          28,
          3,
          {
            optional: true,
            family: "DM Sans",
            lineHeight: 1.18,
          },
        ),
        logoCarrierLayer(828, 160, 184, 96, 3),
        logoLayer(852, 180, 136, 56, 4),
      ];
    case "side_plaque":
      return [
        imageLayer("hero_image", 0, 0, 1080, 1350, "hero_image", 0, 0),
        rectLayer("side_plaque", 92, 230, 390, 770, "#fffaf2", 1, 30),
        textLayer("headline", "headline", "Built for focus", 132, 296, 314, 210, 48, 2),
        textLayer(
          "body",
          "body",
          "A protected side zone keeps the story readable.",
          132,
          540,
          300,
          156,
          27,
          3,
          {
            optional: true,
            family: "DM Sans",
            lineHeight: 1.2,
          },
        ),
        logoLayer(132, 862, 124, 54, 4),
      ];
    case "split_editorial":
      return [
        rectLayer("background", 0, 0, 1080, 1350, "#f5efe4", 0),
        imageLayer("hero_image", 80, 80, 920, 610, "hero_image", 0, 1, { cornerRadius: 34 }),
        rectLayer("copy_panel", 80, 760, 920, 430, "#ffffff", 2, 34),
        textLayer("headline", "headline", "Trust, clarified", 128, 824, 720, 112, 56, 3),
        textLayer(
          "body",
          "body",
          "Important details stay readable in a calm editorial zone.",
          128,
          958,
          720,
          108,
          30,
          4,
          {
            optional: true,
            family: "DM Sans",
            lineHeight: 1.2,
          },
        ),
        logoLayer(828, 1086, 124, 52, 5),
      ];
    case "poster_lockup":
      return [
        rectLayer("background", 0, 0, 1080, 1350, "#17130f", 0),
        imageLayer("hero_image", 0, 0, 1080, 1350, "hero_image", 0, 1),
        textLayer("headline", "headline", "OPEN SEASON", 98, 214, 884, 440, 108, 2, {
          fill: "#fff6e8",
          align: "center",
          letterSpacing: -2.5,
        }),
        textLayer(
          "body",
          "body",
          "A bold note for the first impression.",
          184,
          690,
          712,
          86,
          30,
          3,
          {
            optional: true,
            fill: "#fff6e8",
            family: "DM Sans",
            align: "center",
          },
        ),
        logoCarrierLayer(430, 1086, 220, 104, 4),
        logoLayer(458, 1106, 164, 64, 5),
      ];
    case "caption_overlay":
      return [
        imageLayer("hero_image", 0, 0, 1080, 1350, "hero_image", 0, 0),
        rectLayer("caption_card", 86, 860, 590, 258, "#fffaf2", 1, 26),
        textLayer("headline", "headline", "Quiet proof", 122, 908, 500, 88, 46, 2),
        textLayer(
          "body",
          "body",
          "A small editorial note that feels intentional.",
          122,
          1012,
          476,
          64,
          24,
          3,
          {
            optional: true,
            family: "DM Sans",
            lineHeight: 1.18,
          },
        ),
        logoCarrierLayer(806, 160, 178, 88, 3),
        logoLayer(826, 180, 138, 54, 4),
      ];
    case "image_stack":
      return [
        rectLayer("background", 0, 0, 1080, 1350, "#efe7da", 0),
        imageLayer("hero_image", 74, 84, 600, 620, "hero_image", 0, 1, { cornerRadius: 34 }),
        imageLayer("supporting_image", 590, 510, 390, 420, "supporting_images", 0, 2, {
          cornerRadius: 28,
        }),
        rectLayer("copy_panel", 112, 904, 650, 252, "#fffaf2", 3, 30),
        textLayer("sequence_label", "sequence_label", "Field note 01", 148, 948, 260, 34, 22, 4, {
          optional: true,
          family: "DM Sans",
          letterSpacing: 1,
        }),
        textLayer("headline", "headline", "The story in motion", 148, 996, 548, 92, 48, 5),
        textLayer(
          "body",
          "body",
          "Use two moments to show what changed.",
          148,
          1096,
          510,
          36,
          24,
          6,
          {
            optional: true,
            family: "DM Sans",
          },
        ),
        logoLayer(814, 1114, 128, 54, 7),
      ];
    case "quote_card":
      return [
        rectLayer("background", 0, 0, 1080, 1350, "#f7f3ec", 0),
        imageLayer("hero_image", 90, 92, 392, 950, "hero_image", 0, 1, { cornerRadius: 36 }),
        rectLayer("quote_card", 420, 246, 570, 650, "#ffffff", 2, 36),
        textLayer("headline", "headline", "It finally feels simple.", 472, 318, 454, 252, 58, 3),
        textLayer(
          "body",
          "body",
          "A human proof point gets room to breathe.",
          472,
          606,
          420,
          88,
          27,
          4,
          {
            optional: true,
            family: "DM Sans",
          },
        ),
        textLayer(
          "quote_attribution",
          "quote_attribution",
          "Customer note",
          472,
          730,
          360,
          38,
          22,
          5,
          {
            optional: true,
            family: "DM Sans",
            letterSpacing: 0.8,
          },
        ),
        logoLayer(472, 810, 126, 52, 6),
      ];
    case "product_callout":
      return [
        imageLayer("hero_image", 0, 0, 1080, 1350, "hero_image", 0, 0),
        rectLayer("headline_card", 82, 846, 650, 246, "#fffaf2", 1, 28),
        textLayer("headline", "headline", "The detail that matters", 120, 892, 560, 98, 48, 2),
        textLayer(
          "body",
          "body",
          "Call out one useful benefit without crowding the image.",
          120,
          1006,
          520,
          58,
          24,
          3,
          {
            optional: true,
            family: "DM Sans",
          },
        ),
        rectLayer("callout_chip_1", 692, 270, 250, 78, "#c8ff00", 4, 999),
        textLayer("callout_label_1", "callout_label_1", "Faster setup", 718, 292, 198, 32, 24, 5, {
          family: "DM Sans",
        }),
        textLayer(
          "callout_label_2",
          "callout_label_2",
          "Cleaner handoff",
          728,
          386,
          220,
          32,
          22,
          6,
          {
            optional: true,
            fill: "#ffffff",
            family: "DM Sans",
          },
        ),
        logoCarrierLayer(842, 160, 166, 82, 6),
        logoLayer(862, 180, 126, 52, 7),
      ];
    case "announcement_strip":
      return [
        imageLayer("hero_image", 0, 0, 1080, 1350, "hero_image", 0, 0),
        rectLayer("announcement_strip", 0, 795, 1080, 250, "#161411", 1),
        textLayer("headline", "headline", "New drop Friday", 92, 844, 780, 88, 62, 2, {
          fill: "#fff6e8",
        }),
        textLayer(
          "body",
          "body",
          "Reserve your spot before the first batch closes.",
          92,
          944,
          720,
          44,
          25,
          3,
          {
            optional: true,
            fill: "#fff6e8",
            family: "DM Sans",
          },
        ),
        logoLayer(888, 890, 130, 54, 4),
      ];
    case "minimal_signature":
      return [
        imageLayer("hero_image", 0, 0, 1080, 1350, "hero_image", 0, 0),
        rectLayer("quiet_surface", 88, 128, 904, 300, "#fffaf2", 1, 32),
        textLayer("headline", "headline", "Made to feel effortless", 132, 182, 640, 86, 42, 2, {
          optional: true,
        }),
        logoLayer(132, 300, 146, 58, 3),
      ];
  }
};

const buildTemplate = (spec: TemplatePresetSpec): SocialPostLayoutTemplateDefinitionV2 =>
  socialPostLayoutTemplateDefinitionSchema.parse({
    type: "social_post.layout_template",
    version: 2,
    id: spec.id,
    name: spec.name,
    description: spec.description,
    status: "published",
    canonicalPostType: "feed_portrait",
    supportedPostTypes: [
      "feed_portrait",
      "feed_square",
      "carousel_portrait",
      "carousel_square",
      "tiktok_portrait",
      "tiktok_carousel_portrait",
    ],
    selection: {
      bestFor: spec.bestFor,
      avoidWhen: spec.avoidWhen,
      socialAffinities: spec.socialAffinities,
    },
    slots: {
      imageSlots: spec.imageSlots,
      textSlots: spec.textSlots,
      logoSlot: spec.logoSlot,
    },
    controls: spec.controls,
    paletteHints: {
      brandColorBias: "strong",
      preferredColors: ["#f7f3ec", "#161411", "#c8ff00"],
      avoidColors: [],
    },
    canonicalScene: {
      canvas: CANVAS,
      fonts: FONTS,
      layers: createTemplateLayers(spec.kind),
    },
    previewFill: {
      text: spec.previewText,
      images: spec.previewImages,
      controls: spec.previewControls ?? {},
      includeLogo: true,
    },
    validation: {
      requireAllRequiredSlotsBound: true,
      maxTextBlocks: 16,
      minImageVisibleRatio: 0.5,
    },
  });

const presetSpecs: TemplatePresetSpec[] = [
  {
    id: SOCIAL_POST_LAYOUT_RECIPE_TEMPLATE_IDS.full_bleed_lower_third,
    name: "Full-Bleed Lower Third",
    description:
      "A simple full-bleed photo with a generous lower-third plaque for one short mood, launch, or benefit message.",
    kind: "lower_third",
    bestFor: [
      "single photo-led lifestyle or product mood moments",
      "simple launch or benefit posts with one short low-explanation message",
      "imagery that can reserve a quiet lower area without covering the subject",
    ],
    avoidWhen: [
      "the subject or important props occupy the lower third",
      "the post needs explanatory, technical trust, compliance, or feature-education copy",
      "multiple images, process steps, comparisons, or sequence storytelling are required",
    ],
    socialAffinities: {
      styles: ["aspirational_lifestyle", "social_entertainment"],
      mechanics: ["pov", "relatable_moment", "before_after"],
    },
    imageSlots: [
      heroImageSlot(
        "Full-bleed photo with quiet lower area for the copy plaque.",
        "needs_negative_space",
      ),
    ],
    textSlots: [headlineTextSlot(56), bodyTextSlot(96)],
    logoSlot: optionalLogoSlot("Small brand logo placed away from the lower-third copy plaque."),
    controls: [
      control(
        "plaque_tone",
        "Plaque tone",
        "tone",
        ["light", "dark", "brand"],
        "Sets copy plaque contrast.",
      ),
      control(
        "plaque_anchor",
        "Plaque anchor",
        "placement",
        ["bottom", "lower_left", "lower_right"],
        "Controls lower-third plaque placement.",
      ),
    ],
    previewText: {
      headline: "A calmer launch",
      body: "Make the moment easy to understand.",
    },
    previewImages: {
      hero_image: [
        {
          prompt: "Editorial brand photo with a calm lower copy-safe area.",
          backgroundMode: "opaque",
        },
      ],
    },
    previewControls: { plaque_tone: "light", plaque_anchor: "bottom" },
  },
  {
    id: SOCIAL_POST_LAYOUT_RECIPE_TEMPLATE_IDS.full_bleed_side_plaque,
    name: "Full-Bleed Side Plaque",
    description:
      "A full-bleed photo with one short message anchored to a side plaque when the image has clear side negative space.",
    kind: "side_plaque",
    bestFor: [
      "single vertical lifestyle, portrait, or environment photos with clear side negative space",
      "brand mood or simple editorial announcements where the image remains the main story",
      "short messages that need a taller protected zone than a lower third",
    ],
    avoidWhen: [
      "the image is busy across both sides",
      "the copy needs a separated editorial zone rather than an overlay plaque",
      "service mechanics, product details, proof points, or callout labels are central to the reasoning",
    ],
    socialAffinities: {
      styles: ["expert_authority", "utility_education"],
      mechanics: ["framework", "case_study", "saveable_rule"],
    },
    imageSlots: [
      heroImageSlot(
        "Full-bleed image with clear negative space on one side.",
        "needs_negative_space",
      ),
    ],
    textSlots: [headlineTextSlot(44), bodyTextSlot(82)],
    logoSlot: optionalLogoSlot("Small logo outside the side plaque when space allows."),
    controls: [
      control(
        "plaque_side",
        "Plaque side",
        "placement",
        ["left", "right"],
        "Chooses which side hosts the copy plaque.",
      ),
      control(
        "plaque_tone",
        "Plaque tone",
        "tone",
        ["light", "dark", "brand"],
        "Sets copy plaque contrast.",
      ),
    ],
    previewText: {
      headline: "Built for focus",
      body: "A protected side zone keeps the story readable.",
    },
    previewImages: {
      hero_image: [
        {
          prompt: "Vertical editorial photo with clear side negative space.",
          backgroundMode: "opaque",
        },
      ],
    },
    previewControls: { plaque_side: "left", plaque_tone: "light" },
  },
  {
    id: SOCIAL_POST_LAYOUT_RECIPE_TEMPLATE_IDS.split_editorial,
    name: "Split Editorial",
    description:
      "A clean split between image and copy zones for readable explainers, trust-building, compliance, and technical clarity.",
    kind: "split_editorial",
    bestFor: [
      "trust, compliance, or explanation posts",
      "feature education with readable support copy",
      "brands needing calmer information hierarchy",
    ],
    avoidWhen: [
      "the post should feel immersive or emotional",
      "the image must be full-bleed",
      "there is no meaningful body copy",
    ],
    socialAffinities: {
      styles: ["utility_education", "expert_authority"],
      mechanics: ["comparison", "framework", "checklist"],
    },
    imageSlots: [heroImageSlot("Primary image in a dedicated editorial frame.", "focal_subject")],
    textSlots: [headlineTextSlot(58), bodyTextSlot(120)],
    logoSlot: optionalLogoSlot("Logo can sit in the image or copy margin with safe contrast."),
    controls: [
      control(
        "split",
        "Split direction",
        "placement",
        ["image_top", "image_left", "image_right"],
        "Chooses image/copy zone relationship.",
      ),
      control(
        "copy_tone",
        "Copy tone",
        "tone",
        ["calm", "bold", "technical"],
        "Controls typography emphasis.",
      ),
    ],
    previewText: {
      headline: "Trust, clarified",
      body: "Important details stay readable in a calm editorial zone.",
    },
    previewImages: {
      hero_image: [
        {
          prompt: "Calm editorial workspace image with reliable detail and focus.",
          backgroundMode: "opaque",
        },
      ],
    },
    previewControls: { split: "image_top", copy_tone: "calm" },
  },
  {
    id: SOCIAL_POST_LAYOUT_RECIPE_TEMPLATE_IDS.poster_lockup,
    name: "Poster Lockup",
    description:
      "A typography-first poster system with large confident headline treatment and imagery used as background or texture.",
    kind: "poster_lockup",
    bestFor: [
      "announcements, launches, events, and campaign openers",
      "short punchy headlines",
      "posts where type should carry the first impression",
    ],
    avoidWhen: [
      "the image subject must remain mostly unobstructed",
      "the message requires dense body copy",
      "brand typography is weak or unavailable",
    ],
    socialAffinities: {
      styles: ["aspirational_lifestyle", "social_entertainment"],
      mechanics: ["unpopular_opinion", "contrarian_take", "pov"],
    },
    imageSlots: [
      heroImageSlot(
        "Background image or texture that can sit behind large typography.",
        "background_only",
      ),
    ],
    textSlots: [headlineTextSlot(34), bodyTextSlot(82)],
    logoSlot: optionalLogoSlot("Logo lockup can sit as a small poster mark."),
    controls: [
      control(
        "emphasis",
        "Emphasis",
        "tone",
        ["headline", "date", "brand"],
        "Chooses the poster hierarchy.",
      ),
      control(
        "background_treatment",
        "Background treatment",
        "style",
        ["photo", "soft_crop", "tonal_texture"],
        "Controls how imagery supports the type.",
      ),
    ],
    previewText: {
      headline: "OPEN SEASON",
      body: "A bold note for the first impression.",
    },
    previewImages: {
      hero_image: [
        {
          prompt: "Atmospheric campaign opener image that can support large overlay type.",
          backgroundMode: "opaque",
        },
      ],
    },
    previewControls: { emphasis: "headline", background_treatment: "photo" },
  },
  {
    id: SOCIAL_POST_LAYOUT_RECIPE_TEMPLATE_IDS.magazine_caption_overlay,
    name: "Magazine Caption Overlay",
    description:
      "A photo-led editorial caption system where concise copy sits in a quiet area like a deliberate magazine note.",
    kind: "caption_overlay",
    bestFor: [
      "subtle brand storytelling",
      "community, behind-the-scenes, or culture posts",
      "images with natural quiet areas",
    ],
    avoidWhen: [
      "copy needs high-impact ad treatment",
      "the image is visually dense everywhere",
      "legal or dense body copy is required",
    ],
    socialAffinities: {
      styles: ["community_led", "aspirational_lifestyle"],
      mechanics: ["confession", "relatable_moment", "pov"],
    },
    imageSlots: [
      heroImageSlot(
        "Photo with a naturally quiet area for a small editorial caption.",
        "overlay_anchor",
      ),
    ],
    textSlots: [headlineTextSlot(44), bodyTextSlot(80)],
    logoSlot: optionalLogoSlot("Logo may sit in a quiet corner, separate from the caption note."),
    controls: [
      control(
        "caption_placement",
        "Caption placement",
        "placement",
        ["top_left", "top_right", "bottom_left", "bottom_right"],
        "Places the caption note.",
      ),
      control(
        "caption_tone",
        "Caption tone",
        "tone",
        ["quiet", "witty", "documentary"],
        "Controls caption styling.",
      ),
    ],
    previewText: {
      headline: "Quiet proof",
      body: "A small editorial note that feels intentional.",
    },
    previewImages: {
      hero_image: [
        {
          prompt: "Documentary-style brand photo with a quiet lower caption zone.",
          backgroundMode: "opaque",
        },
      ],
    },
    previewControls: { caption_placement: "bottom_left", caption_tone: "quiet" },
  },
  {
    id: SOCIAL_POST_LAYOUT_RECIPE_TEMPLATE_IDS.image_stack_story,
    name: "Image Stack Story",
    description:
      "A sequence-led layout with two image frames and one concise text lockup for process, recap, or comparison stories.",
    kind: "image_stack",
    bestFor: [
      "events, recaps, process stories, and before-after narratives",
      "community moments with multiple visuals",
      "posts where image variety is the concept",
    ],
    avoidWhen: [
      "only one strong hero visual exists",
      "the headline needs large poster scale",
      "small-screen clarity would suffer from many details",
    ],
    socialAffinities: {
      styles: ["expert_authority", "utility_education"],
      mechanics: ["case_study", "before_after", "tutorial"],
    },
    imageSlots: [
      heroImageSlot("Lead moment in the stack.", "focal_subject"),
      supportingImageSlot("Additional moments that complete the story sequence."),
    ],
    textSlots: [
      headlineTextSlot(54),
      bodyTextSlot(92),
      {
        id: "sequence_label",
        label: "Sequence label",
        kind: "label",
        requirement: "optional",
        purpose: "Small label for sequence, recap, or step framing.",
        suggestedMaxLength: 24,
      },
    ],
    logoSlot: optionalLogoSlot("Logo sits below the story stack without crowding copy."),
    controls: [
      control(
        "stack_style",
        "Stack style",
        "placement",
        ["overlap", "grid", "filmstrip"],
        "Chooses how image frames relate.",
      ),
    ],
    previewText: {
      sequence_label: "Field note 01",
      headline: "The story in motion",
      body: "Use two moments to show what changed.",
    },
    previewImages: {
      hero_image: [
        { prompt: "Lead editorial moment for a process or recap story.", backgroundMode: "opaque" },
      ],
      supporting_images: [
        {
          prompt: "Supporting detail moment that extends the visual story.",
          backgroundMode: "opaque",
        },
      ],
    },
    previewControls: { stack_style: "overlap" },
  },
  {
    id: SOCIAL_POST_LAYOUT_RECIPE_TEMPLATE_IDS.quote_card_photo,
    name: "Quote Card Photo",
    description:
      "A proof or testimonial layout pairing a photo with a large quote-style headline and optional attribution.",
    kind: "quote_card",
    bestFor: [
      "founder, customer, community, or testimonial posts",
      "proof points that sound human",
      "voice-led brand storytelling",
    ],
    avoidWhen: [
      "there is no authentic quote or voice-led message",
      "the post is primarily legal or feature-detail content",
      "the brand should avoid testimonial framing",
    ],
    socialAffinities: {
      styles: ["community_led", "expert_authority"],
      mechanics: ["confession", "community_debate", "things_people_say"],
    },
    imageSlots: [
      heroImageSlot(
        "Portrait or contextual proof photo paired with the quote card.",
        "focal_subject",
      ),
    ],
    textSlots: [
      headlineTextSlot(74),
      bodyTextSlot(86),
      {
        id: "quote_attribution",
        label: "Quote attribution",
        kind: "attribution",
        requirement: "optional",
        purpose: "Person, customer, or source label for the quote.",
        suggestedMaxLength: 36,
      },
    ],
    logoSlot: optionalLogoSlot("Logo or quote-mark accent can reinforce the proof card."),
    controls: [
      control(
        "quote_style",
        "Quote style",
        "tone",
        ["editorial", "testimonial", "founder_note"],
        "Controls proof-card tone.",
      ),
    ],
    previewText: {
      headline: "It finally feels simple.",
      body: "A human proof point gets room to breathe.",
      quote_attribution: "Customer note",
    },
    previewImages: {
      hero_image: [
        {
          prompt: "Human proof photo with editorial warmth and clear subject focus.",
          backgroundMode: "opaque",
        },
      ],
    },
    previewControls: { quote_style: "editorial" },
  },
  {
    id: SOCIAL_POST_LAYOUT_RECIPE_TEMPLATE_IDS.product_detail_callout,
    name: "Product Detail Callout",
    description:
      "A product, feature, or service-mechanics image with restrained labels for details that need explicit callouts.",
    kind: "product_callout",
    bestFor: [
      "feature benefits, product details, and service mechanics",
      "simple callouts that clarify what viewers are seeing",
      "brands with concrete product or payment moments",
    ],
    avoidWhen: [
      "the image has no clear product/detail focal point",
      "more than two callouts are needed",
      "the concept risks looking like an app UI screenshot",
    ],
    socialAffinities: {
      styles: ["expert_authority", "utility_education"],
      mechanics: ["case_study", "checklist", "tutorial"],
    },
    imageSlots: [
      heroImageSlot(
        "Product or feature detail with clear focal areas for labels.",
        "focal_subject",
      ),
    ],
    textSlots: [
      headlineTextSlot(58),
      bodyTextSlot(72),
      {
        id: "callout_label_1",
        label: "Callout label 1",
        kind: "callout",
        requirement: "required",
        purpose: "Primary short label rendered as callout text.",
        suggestedMaxLength: 28,
      },
      {
        id: "callout_label_2",
        label: "Callout label 2",
        kind: "callout",
        requirement: "optional",
        purpose: "Optional secondary short label rendered as callout text.",
        suggestedMaxLength: 28,
      },
    ],
    logoSlot: optionalLogoSlot("Logo remains secondary to the product detail and callouts."),
    controls: [
      control(
        "callout_style",
        "Callout style",
        "style",
        ["pills", "leader_lines", "numbered"],
        "Controls label treatment without freeform arrows.",
      ),
    ],
    previewText: {
      headline: "The detail that matters",
      body: "Call out one useful benefit without crowding the image.",
      callout_label_1: "Faster setup",
      callout_label_2: "Cleaner handoff",
    },
    previewImages: {
      hero_image: [
        {
          prompt: "Product or service detail image with clear focal points for callouts.",
          backgroundMode: "opaque",
        },
      ],
    },
    previewControls: { callout_style: "pills" },
  },
  {
    id: SOCIAL_POST_LAYOUT_RECIPE_TEMPLATE_IDS.announcement_strip,
    name: "Announcement Strip",
    description:
      "A high-contrast announcement strip combined with a strong image, built for dates, drops, launches, or notices.",
    kind: "announcement_strip",
    bestFor: [
      "events, limited-time drops, launches, and workshop prompts",
      "messages with a clear date, place, or action",
      "direct campaign announcements",
    ],
    avoidWhen: [
      "the post should feel quiet or premium",
      "the message is not time-sensitive",
      "the image cannot tolerate a bold strip overlay",
    ],
    socialAffinities: {
      styles: ["aspirational_lifestyle", "community_led"],
      mechanics: ["expectation_vs_reality", "things_people_say", "community_debate"],
    },
    imageSlots: [
      heroImageSlot(
        "Strong image that can tolerate a bold announcement strip overlay.",
        "needs_negative_space",
      ),
    ],
    textSlots: [headlineTextSlot(42), bodyTextSlot(84)],
    logoSlot: optionalLogoSlot("Logo can sit above or below the strip with clear contrast."),
    controls: [
      control(
        "strip_position",
        "Strip position",
        "placement",
        ["top", "middle", "bottom"],
        "Places the announcement strip.",
      ),
      control(
        "urgency",
        "Urgency",
        "tone",
        ["low", "medium", "high"],
        "Controls strip contrast and emphasis.",
      ),
    ],
    previewText: {
      headline: "New drop Friday",
      body: "Reserve your spot before the first batch closes.",
    },
    previewImages: {
      hero_image: [
        {
          prompt: "Energetic launch image that leaves room for a bold announcement strip.",
          backgroundMode: "opaque",
        },
      ],
    },
    previewControls: { strip_position: "bottom", urgency: "medium" },
  },
  {
    id: SOCIAL_POST_LAYOUT_RECIPE_TEMPLATE_IDS.minimal_logo_signature,
    name: "Minimal Logo Signature",
    description:
      "A sparse premium composition with strong negative space, a short headline, and a restrained logo signature.",
    kind: "minimal_signature",
    bestFor: [
      "brand-building posts",
      "premium minimal campaigns",
      "single-message posts where restraint is the point",
    ],
    avoidWhen: [
      "the post needs explanatory body copy",
      "there is no logo asset and brand recognition matters",
      "the prompt asks for multiple ideas or visuals",
    ],
    socialAffinities: {
      styles: ["aspirational_lifestyle", "expert_authority"],
      mechanics: ["saveable_rule", "framework", "before_after"],
    },
    imageSlots: [
      heroImageSlot(
        "Sparse still life or atmospheric image with generous negative space.",
        "background_only",
      ),
    ],
    textSlots: [headlineTextSlot(46, "optional")],
    logoSlot: optionalLogoSlot(
      "Brand logo mark or lockup acts as the restrained signature element.",
    ),
    controls: [
      control(
        "negative_space",
        "Negative space",
        "placement",
        ["top", "center", "bottom", "side"],
        "Chooses where the composition breathes.",
      ),
      control(
        "signature_tone",
        "Signature tone",
        "tone",
        ["premium", "quiet", "confident"],
        "Controls logo/text restraint.",
      ),
    ],
    previewText: {
      headline: "Made to feel effortless",
    },
    previewImages: {
      hero_image: [
        {
          prompt: "Sparse premium still life with generous negative space.",
          backgroundMode: "opaque",
        },
      ],
    },
    previewControls: { negative_space: "top", signature_tone: "premium" },
  },
];

export const DEFAULT_SOCIAL_POST_LAYOUT_TEMPLATES = presetSpecs.map(buildTemplate);
